/**
 * React Query hooks for the user persona (Phase P5).
 *
 * usePersona reads the newest persona version (or null) — consumed by the
 * thesis form's knowledge questions (T23) and the chat persona-shaping
 * surfaces (T25). useCreatePersonaVersion appends a version and seeds the
 * ['persona'] cache with the returned read model.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as personaApi from '../personaApi';
import type { PersonaCreateRequest, UserPersonaRead } from '../personaApi';

/**
 * Hook to fetch the caller's newest persona version (null when none).
 */
export function usePersona() {
  return useQuery<UserPersonaRead | null>({
    queryKey: ['persona'],
    queryFn: personaApi.getPersona,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

/**
 * Hook to append a new persona version.
 * Seeds the persona cache directly — the POST returns the newest read model.
 */
export function useCreatePersonaVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PersonaCreateRequest) => personaApi.createPersonaVersion(data),
    onSuccess: (persona: UserPersonaRead) => {
      queryClient.setQueryData(['persona'], persona);
    },
    onError: (error: Error) => {
      console.error('Failed to save persona:', error);
      throw error;
    },
  });
}
